import type { Theme, ThemeOptions } from '@mui/material/styles';
import { createTheme } from '@mui/material/styles';
import { components } from './components.ts';
import {
  base,
  dataViz,
  intent,
  shadows,
  shape,
  spacing,
  state,
  stateAlpha,
  surface,
  text,
  typography,
} from './tokens/index.ts';

/**
 * The design-system theme — the single place customization lives (CLAUDE.md "Golden rule").
 *
 * Assembled entirely from design tokens (`./tokens`, mirrored from Figma) plus per-component
 * overrides (`./components`). Colors are real brand values; typography/spacing/shape/shadows
 * are still PLACEHOLDERS pending their Figma import.
 *
 * Intents map onto MUI's native palette slots. We provide each slot's `main` (= intent
 * `solid`) and `contrastText` (= `onSolid`); MUI derives `dark`, except success/error/warning,
 * where `dark` = `text` (the 120 shades) so legacy `palette.*.dark` text stays readable. The richer facets
 * (subtle/text/border/disabled, the `neutral` intent, surface/state tokens) are not native
 * to MUI's palette — read them from the exported `tokens` in component overrides, and add
 * palette augmentation when the first component needs them.
 */
export const themeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    primary: {
      main: intent.primary.solid,
      contrastText: intent.primary.onSolid,
      light: intent.primary.subtle,
      text: intent.primary.text,
      border: intent.primary.border,
      disabled: intent.primary.disabled,
    },
    secondary: {
      main: intent.secondary.solid,
      contrastText: intent.secondary.onSolid,
      light: intent.secondary.subtle,
      text: intent.secondary.text,
      border: intent.secondary.border,
      disabled: intent.secondary.disabled,
    },
    info: {
      main: intent.info.solid,
      contrastText: intent.info.onSolid,
      light: intent.info.subtle,
      text: intent.info.text,
      border: intent.info.border,
      disabled: intent.info.disabled,
    },
    success: {
      main: intent.success.solid,
      contrastText: intent.success.onSolid,
      light: intent.success.subtle,
      dark: intent.success.text,
      text: intent.success.text,
      border: intent.success.border,
      disabled: intent.success.disabled,
    },
    error: {
      main: intent.error.solid,
      contrastText: intent.error.onSolid,
      light: intent.error.subtle,
      dark: intent.error.text,
      text: intent.error.text,
      border: intent.error.border,
      disabled: intent.error.disabled,
    },
    warning: {
      main: intent.warning.solid,
      contrastText: intent.warning.onSolid,
      light: intent.warning.subtle,
      dark: intent.warning.text,
      text: intent.warning.text,
      border: intent.warning.border,
      disabled: intent.warning.disabled,
    },
    text: {
      primary: text.primary,
      secondary: text.secondary,
      disabled: text.disabled,
      white: text.white,
      link: text.link,
    },
    background: {
      default: surface.default,
      paper: surface.white,
      boxLight: surface.raised,
      boxDark: surface.default,
      boxDarker: surface.sunken,
      grayLight: base.grey010,
      boxDarkest: base.grey016,
      antiflashWhite: surface.sunken,
      tooltip: surface.inverse,
    },
    divider: intent.neutral.border,
    action: {
      hover: state.hover,
      hoverOpacity: stateAlpha.hover,
      selected: state.selected,
      selectedOpacity: stateAlpha.selected,
      // focus tint: no dedicated token yet → MUI defaults it (rgba(0,0,0,0.12)).
      disabled: state.disabled.content,
      disabledBackground: state.disabled.surface,
      disabledOpacity: stateAlpha.disabledContent,
    },
    // Chart hues by name, for code that wants a specific one. The 14 raw hex values that
    // originally sat here broke HARD RULE 1 and had `lint:tokens` red on main; they now live in
    // `tokens/dataViz.ts` (`orchid` spelled correctly). To paint a SERIES, don't reach in here
    // by name — use `getSeriesColor(index)`, which walks the validated order.
    charts: dataViz.categorical,
  },
  typography,
  spacing,
  shape,
  shadows,
  components,
};

export const theme: Theme = createTheme(themeOptions);

export type AppTheme = typeof theme;
